import React, { useEffect, useState } from 'react';
import { Copy, RefreshCcw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Text from '@/utils/responsiveText';
import { Input } from '../ui/input';
import { useNavigate } from 'react-router-dom';
import { reactIcons } from '@/utils/icons';
import { DateRange, DateRangePicker } from './DateRangePicker';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import toast from 'react-hot-toast';

interface HeaderProps {
  title: string | undefined | null;
  count?: string | number;
  isRefreshing?: boolean;
  onRefresh?: () => void;
  showButton?: boolean;
  showButtonTwo?: boolean;
  buttonLabel?: string;
  buttonLabelTwo?: string;
  onButtonClick?: () => void;
  onButtonClickTwo?: () => void;
  resetPage?: () => void;
  showExport?: boolean;
  input?: boolean;
  search?: string;
  setSearch?: (value: string) => void;
  dashboard?: boolean;
  agentCode?: string | null;
  backbtn?: boolean;
  path?: string;
  handleExport?: (type: string) => void;
  // calendar
  showDateRangePicker?: boolean;
  dateRangeValue?: DateRange;
  onDateRangeChange?: (range: DateRange) => void;
  onDateRangeApply?: () => void;
  canDownloadReports?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  title,
  count,
  isRefreshing = false,
  onRefresh,
  showButton = false,
  showButtonTwo = false,
  buttonLabel,
  buttonLabelTwo,
  onButtonClick,
  onButtonClickTwo,
  showExport = false,
  input = false,
  search,
  setSearch,
  resetPage,
  dashboard = false,
  backbtn = false,
  agentCode,
  path,
  handleExport,
  // calendar
  showDateRangePicker = false,
  dateRangeValue,
  onDateRangeChange,
  onDateRangeApply,
  canDownloadReports = true,
}) => {
  const { isAgent } = useRoleCheck();
  // const imagekit = import.meta.env.VITE_IMAGEKIT_URL;
  const navigate = useNavigate();
  const images: string[] = [
    ...(!isAgent ? ['Excel'] : []),
    // 'Pdf',
    //  'Csv',
    'refresh',
  ];
  const iconMap: { [key: string]: React.ElementType } = {
    Excel: reactIcons.excel,
    // Pdf: reactIcons.pdf,
    // Csv: reactIcons.csv,
    refresh: reactIcons.refresh,
  };
  const handleExportClick = (imgName: string) => {
    if (imgName === 'refresh' && onRefresh) {
      onRefresh();
    } else {
      handleExport?.(imgName);
    }
  };
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formattedDate = currentTime.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const formattedTime = currentTime.toLocaleTimeString('en-US', {
    hour12: false,
  });

  const handleCopyAgentCode = async () => {
    try {
      await navigator.clipboard.writeText(agentCode ?? '');
      toast.success(`Agent Code: ${agentCode} copied`);
    } catch (error) {
      toast.error('Failed to copy Agent Code');
    }
  };

  return (
    <>
      {dashboard ? (
        <div
          className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${
            title === 'DASHBOARD' ? 'padding' : 'border-b border-gray-300'
          } pb-4`}
        >
          <div className="flex items-center gap-2">
            {backbtn && (
              <reactIcons.arrowLeft
                size={32}
                onClick={() => (path ? navigate(path) : navigate(-1))}
                className="cursor-pointer p-1 border border-gray-300 rounded-md"
              />
            )}
            <Text min={24} max={28} className="font-bold" as="h2">
              {title}
            </Text>
          </div>
          <div className="flex flex-wrap items-center gap-2 font-medium">
            <Text min={12} max={16} className="text-base">
              {formattedDate}
            </Text>
            <Text min={18} max={24} className="text-2xl">
              {formattedTime}
            </Text>
            <button
              onClick={onRefresh}
              className="flex items-center gap-2 bg-card px-2.5 py-2 rounded-full border border-gray-300"
            >
              <RefreshCcw
                className={`transition-transform duration-1000 ${isRefreshing ? 'animate-spin' : ''}`}
              />
              Refresh
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between border-b border-gray-300 pb-4">
          {/* LEFT */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {backbtn && (
              <reactIcons.arrowLeft
                size={32}
                onClick={() => (path ? navigate(path) : navigate(-1))}
                className="cursor-pointer p-1 border border-gray-300 rounded-md"
              />
            )}
            <div className="h-full flex gap-1 items-end font-bold text-gray-900">
              <span className="text-[26px]">{title}</span>
              {count && <span className="text-[18px] font-medium leading-[34px]">{count}</span>}
            </div>
          </div>

          {/* RIGHT */}
          <div className="flex flex-wrap items-center gap-2">
            {agentCode && (
              <span className="bg-white text-sm border flex items-center gap-2 border-gray-200 rounded-lg px-2 py-1.5 shadow-sm">
                Agent Code:<b>{agentCode} </b>{' '}
                <button type="button" onClick={handleCopyAgentCode}>
                  <Copy size={16} />
                </button>
              </span>
            )}

            {/* Refresh */}
            {onRefresh && !showExport && (
              <div className="border border-gray-200 rounded-lg bg-white p-1 shadow-sm">
                <RefreshCcw
                  onClick={onRefresh}
                  className={`h-6 w-6 text-gray-600 transition-transform duration-1000 cursor-pointer ${
                    isRefreshing ? 'animate-spin' : ''
                  }`}
                />
              </div>
            )}

            {/* Create Buttons */}
            {showButton && (
              <Button variant="outline" onClick={onButtonClick} className="border-gray-300">
                <Text min={14} max={16} className="font-semibold">
                  {buttonLabel || `+ Create ${title}`}
                </Text>
              </Button>
            )}
            {showButtonTwo && (
              <Button variant="outline" onClick={onButtonClickTwo} className="border-gray-300">
                <Text min={14} max={16} className="font-semibold">
                  {buttonLabelTwo || `+ Create ${title}`}
                </Text>
              </Button>
            )}

            {/* Export icons */}
            {showExport && (
              <div className="flex items-center gap-2">
                {images.map((imgName) => {
                  const IconComponent = iconMap[imgName];
                  if (!IconComponent) return null;
                  return (
                    <Button
                      key={imgName}
                      className={`${!canDownloadReports && imgName !== 'refresh' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} border-gray-300 px-1 min-w-[38px]`}
                      variant="outline"
                      onClick={() =>
                        (canDownloadReports || imgName === 'refresh') && handleExportClick(imgName)
                      }
                    >
                      <IconComponent
                        className={`cursor-pointer ${
                          imgName === 'refresh' && isRefreshing ? 'animate-spin' : ''
                        }`}
                        size={24}
                      />
                    </Button>
                  );
                })}
              </div>
            )}

            {/* Search */}
            {input && (
              <div className="relative w-full sm:w-auto sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />
                <Input
                  type="text"
                  placeholder="Search by .."
                  className="bg-transparent pl-9 w-full"
                  value={search}
                  onChange={(e) => {
                    setSearch?.(e.target.value);
                    resetPage?.();
                  }}
                />
              </div>
            )}

            {/* Date Range Picker */}
            {showDateRangePicker && (
              <div className="w-full sm:w-auto sm:min-w-[260px]">
                <DateRangePicker
                  value={dateRangeValue}
                  onChange={onDateRangeChange}
                  onApply={onDateRangeApply}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
